/**
 * Architeia URL Configuration
 * Based on the architeia-infra repository URL structure
 */
import { findRuntimeConfig } from '@architeia/config';
/**
 * Get the API base URL for the current environment
 */
export function getApiBaseUrl(env = getEnvironment()) {
    const urls = {
        development: 'https://api.dev.pinakes.de',
        staging: 'https://api.staging.pinakes.de',
        production: 'https://api.pinakes.de'
    };
    return urls[env];
}
/**
 * Get the Kora frontend URL for the current environment
 */
export function getKoraUrl(env = getEnvironment()) {
    const urls = {
        development: 'https://kora.dev.pinakes.de',
        staging: 'https://kora.staging.pinakes.de',
        production: 'https://kora.id'
    };
    return urls[env];
}
/**
 * Get the Kora API URL for the current environment
 * This is the backend API endpoint, not the frontend URL
 */
export function getKoraApiUrl(env = getEnvironment()) {
    const urls = {
        development: 'https://api.dev.pinakes.de/kora',
        staging: 'https://api.staging.pinakes.de/kora',
        production: 'https://api.pinakes.de/kora'
    };
    return urls[env];
}
/**
 * Get the Auth0 audience for the current environment
 */
export function getAuth0Audience(env = getEnvironment()) {
    const audiences = {
        development: 'https://api.dev.pinakes.de',
        staging: 'https://api.staging.pinakes.de',
        production: 'https://api.pinakes.de'
    };
    return audiences[env];
}
/**
 * Get the current environment.
 *
 * AINFRA-171: a deployed frontend reads it from the runtime configuration (`/config.js`, through
 * `@architeia/config`), so one image runs in every environment. Without a runtime configuration
 * (Node, unit tests) it falls back to Vite's MODE, then NODE_ENV, then development.
 */
export function getEnvironment() {
    const runtime = findRuntimeConfig();
    if (runtime !== undefined)
        return runtime.environment;
    // Check import.meta.env (Vite)
    // Note: import.meta is always available in ESM modules, but we check safely
    try {
        if (typeof import.meta !== 'undefined' && import.meta.env) {
            const mode = import.meta.env.MODE;
            if (mode === 'production')
                return 'production';
            if (mode === 'staging')
                return 'staging';
        }
    }
    catch {
        // import.meta not available (e.g., in Node.js without ESM)
    }
    // Check NODE_ENV (Node.js)
    if (typeof process !== 'undefined' && process.env) {
        const nodeEnv = process.env.NODE_ENV;
        if (nodeEnv === 'production')
            return 'production';
        if (nodeEnv === 'staging')
            return 'staging';
    }
    // Default to development
    return 'development';
}
/**
 * Service URLs configuration
 */
export const SERVICE_URLS = {
    development: {
        api: 'https://api.dev.pinakes.de',
        kora: 'https://kora.dev.pinakes.de',
        core: 'https://core.dev.pinakes.de',
        secretOrg: 'https://secretorg.dev.pinakes.de',
        pinakes: 'https://pinakes.dev.pinakes.de',
        tamara: 'https://tamara.dev.pinakes.de',
        mingleMage: 'https://minglemage.dev.pinakes.de',
        atlas: 'https://atlas.dev.pinakes.de'
    },
    staging: {
        api: 'https://api.staging.pinakes.de',
        kora: 'https://kora.staging.pinakes.de',
        core: 'https://core.staging.pinakes.de',
        secretOrg: 'https://secretorg.staging.pinakes.de',
        pinakes: 'https://pinakes.staging.pinakes.de',
        tamara: 'https://tamara.staging.pinakes.de',
        mingleMage: 'https://minglemage.staging.pinakes.de',
        atlas: 'https://atlas.staging.pinakes.de'
    },
    production: {
        api: 'https://api.pinakes.de',
        kora: 'https://kora.id',
        core: 'https://core.pinakes.de',
        secretOrg: 'https://the-secret.org',
        pinakes: 'https://pinakes.info',
        tamara: 'https://tamara.pinakes.info',
        mingleMage: 'https://minglemage.com',
        atlas: 'https://atlas.pinakes.de'
    }
};
/**
 * Get service URL for a specific service and environment
 */
export function getServiceUrl(service, env = getEnvironment()) {
    return SERVICE_URLS[env][service];
}
/**
 * Get the Secret Org API URL for the current environment.
 *
 * Unlike Kora — which is path-prefixed behind the shared `api.{env}.pinakes.de` gateway
 * (see {@link getKoraApiUrl}) — Secret Org is served from its own host: the `secretorg.*`
 * subdomain in development/staging and the custom domain `the-secret.org` in production.
 * Thin wrapper over {@link getServiceUrl} so callers have a Kora-symmetric helper and a
 * single source of truth for the Secret Org origin.
 *
 * NOTE: `createSecretOrgClient` intentionally does NOT use this for its same-origin SPA
 * fallback (it uses relative `''`); this helper is for cross-origin consumers.
 */
export function getSecretOrgApiUrl(env = getEnvironment()) {
    return getServiceUrl('secretOrg', env);
}
/**
 * Get the Mingle Mage API URL for the current environment.
 *
 * Unlike Kora — which is path-prefixed behind the shared `api.{env}.pinakes.de` gateway
 * (see {@link getKoraApiUrl}) — Mingle Mage is served from its own host: the `minglemage.*`
 * subdomain in development/staging and the custom domain `minglemage.com` in production.
 * Thin wrapper over {@link getServiceUrl}.
 */
export function getMingleMageApiUrl(env = getEnvironment()) {
    return getServiceUrl('mingleMage', env);
}
/**
 * Get the Conjuring Atlas API URL for the current environment.
 *
 * Unlike Kora — which is path-prefixed behind the shared `api.{env}.pinakes.de` gateway
 * (see {@link getKoraApiUrl}) — Atlas is served from its own host, and the API is path-routed on
 * the frontend host (`/api` and `/health` go to `atlas-api`, everything else to the SPA), the same
 * as Mingle Mage (AINFRA-73). The host stays in the `*.pinakes.de` family across all three
 * environments (`atlas.dev.*`, `atlas.staging.*`, and `atlas.pinakes.de` in production), so unlike
 * Secret Org or Mingle Mage there is no custom production domain. Thin wrapper over
 * {@link getServiceUrl}.
 */
export function getAtlasApiUrl(env = getEnvironment()) {
    return getServiceUrl('atlas', env);
}
export const PRODUCT_FRONTEND_KEYS = [
    'kora',
    'mingleMage',
    'atlas',
    'secretOrg'
];
const MAX_RETURN_URL_LENGTH = 2048;
/** Local dev servers by port (AGENTS.md key scripts). Kora Web itself (3002) is never a target. */
const DEV_ORIGIN_PRODUCTS = {
    '5173': 'mingleMage',
    '3005': 'atlas',
    '3010': 'secretOrg'
};
function parseHttpUrl(raw) {
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
    }
    catch {
        return null;
    }
}
function devOriginProduct(origin, devOrigins) {
    for (const entry of devOrigins) {
        const allowed = parseHttpUrl(entry);
        if (allowed === null || allowed.origin !== origin)
            continue;
        if (allowed.protocol !== 'http:')
            continue;
        if (allowed.hostname !== 'localhost' && allowed.hostname !== '127.0.0.1')
            continue;
        return DEV_ORIGIN_PRODUCTS[allowed.port] ?? null;
    }
    return null;
}
/**
 * Accepts only an absolute http(s) URL whose origin is an allowlisted product frontend for `env`.
 * In development it also accepts http://localhost:<port> and http://127.0.0.1:<port>, but only
 * through `devOrigins` (and only for the known local dev ports). It rejects credentials,
 * non-http(s) schemes, strings over 2048 chars, `selfOrigin`, and every non-string.
 */
export function parseProductReturnUrl(raw, options) {
    if (typeof raw !== 'string' || raw === '' || raw.length > MAX_RETURN_URL_LENGTH)
        return null;
    const url = parseHttpUrl(raw);
    if (url === null)
        return null;
    if (url.username !== '' || url.password !== '')
        return null;
    if (url.origin === options.selfOrigin)
        return null;
    for (const product of PRODUCT_FRONTEND_KEYS) {
        const allowed = parseHttpUrl(SERVICE_URLS[options.env][product]);
        if (allowed !== null && allowed.origin === url.origin)
            return { url: url.href, product };
    }
    if (options.env === 'development' && options.devOrigins !== undefined) {
        const product = devOriginProduct(url.origin, options.devOrigins);
        if (product !== null)
            return { url: url.href, product };
    }
    return null;
}
/**
 * Internal Docker network URLs (for server-side use)
 */
export const INTERNAL_URLS = {
    kora: 'http://kora:3000',
    core: 'http://core:3000',
    secretOrg: 'http://secretorg:3000',
    pinakes: 'http://pinakes:4000',
    tamara: 'http://tamara:5000',
    mingleMage: 'http://minglemage:3000',
    postgres: 'postgres:5432',
    redis: 'redis:6379'
};
